import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, ShoppingCart, Workflow, Store, Server, UserCheck, Repeat } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import { PaymentDetails } from '@/lib/api/payments';
import { DateDisplay } from '@/components/date-display';
import { formatMoney } from '@/lib/money';
import { OrderResourceBadge } from '@/components/orders/OrderResourceBadge';
import { PaymentFlowResourceBadge } from '@/components/payment-flow/PaymentFlowResourceBadge';
import { StoreResourceBadge } from '@/app/stores/components/StoreResourceBadge';
import { CryptoOnchainMethodBadge } from '@/components/payments/crypto-onchain-method-badge';
import { getCryptoOnchainAssetLabel, getPaymentMethodDisplay } from '@/components/payments/payment-method-cell';

interface PaymentOverviewCardProps {
  payment: PaymentDetails;
}

/**
 * PaymentOverviewCard - Main overview section for payment details
 * Displays key payment information in a consistent grid layout (5 columns)
 */
// Extended type for payment details with store relation
// Note: These fields are returned by backend but tRPC types may lag
type ExtendedPaymentDetails = PaymentDetails & {
  storeId?: string | null;
  store?: { id: string; name: string } | null;
};

export function PaymentOverviewCard({ payment }: PaymentOverviewCardProps) {
  const { t } = useTranslation('payments');

  // Cast to extended type - backend returns these fields
  const extendedPayment = payment as ExtendedPaymentDetails;

  // Store info - prefer direct relation, fallback to order's store
  const storeId = extendedPayment.storeId ?? extendedPayment.store?.id;
  const storeName = extendedPayment.store?.name;

  const processor = (payment as any).processor as { id: string; name: string; type: string } | null;
  const isMIT = payment.initiatedBy === 'merchant';

  // Integration payments (e.g. HiPay) have no paymentInstrument/processor; show from metadata
  const integrationDisplay = React.useMemo(() => {
    const meta = payment.metadata as Record<string, unknown> | null | undefined;
    const createdBy = meta?.createdBy as string | undefined;
    if (!createdBy || typeof createdBy !== 'string' || !createdBy.startsWith('integration:')) return null;
    const provider = createdBy.replace('integration:', '');
    const label = provider.charAt(0).toUpperCase() + provider.slice(1);
    return {
      paymentMethodLabel: `${label} Card`,
      processorLabel: label,
    };
  }, [payment.metadata]);

  // On-chain crypto: no instrument and no processor, `Crypto`, the coin and network, Test on a testnet.
  const methodDisplay = getPaymentMethodDisplay(payment);
  const cryptoDisplay = methodDisplay.kind === 'crypto' ? methodDisplay : null;

  // Format card display
  const cardDisplay = React.useMemo(() => {
    const card = payment.paymentInstrument?.card;
    if (!card) return null;

    const brand = card.brand || 'Card';
    const last4 = card.last4 || '****';
    return {
      brand: brand.charAt(0).toUpperCase() + brand.slice(1),
      last4,
      expiry: `${card.exp_month}/${card.exp_year}`,
    };
  }, [payment.paymentInstrument?.card]);

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <CardTitle className="text-2xl">{t('payment')}</CardTitle>
              <CopyableResourceBadge name={t('paymentId')} value={payment.id} />
            </div>
            <CardDescription>
              {t('createdOn')} <DateDisplay value={payment.createdAt} />
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* Amount */}
          <div className="flex flex-col">
            <span className="text-muted-foreground mb-1 text-sm">{t('amount')}</span>
            <span className="text-2xl font-bold">{formatMoney(payment.amount, payment.currency)}</span>
            {payment.refundedAmount && payment.refundedAmount > 0 && (
              <span className="text-destructive mt-1 text-sm">
                -{formatMoney(payment.refundedAmount, payment.currency)} {t('refunded')}
              </span>
            )}
          </div>

          {/* Payment Method */}
          <div className="flex flex-col">
            <span className="text-muted-foreground mb-1 text-sm">{t('paymentMethod')}</span>
            {cardDisplay ? (
              <>
                <div className="flex items-center">
                  <CreditCard className="mr-2 h-5 w-5" />
                  <span className="font-medium">
                    {cardDisplay.brand} •••• {cardDisplay.last4}
                  </span>
                </div>
                <span className="text-muted-foreground mt-1 text-sm">
                  {t('expires')} {cardDisplay.expiry}
                </span>
              </>
            ) : cryptoDisplay ? (
              <CryptoOnchainMethodBadge
                assetLabel={getCryptoOnchainAssetLabel(cryptoDisplay)}
                isTest={cryptoDisplay.isTest}
              />
            ) : integrationDisplay ? (
              <div className="flex items-center">
                <CreditCard className="mr-2 h-5 w-5" />
                <span className="font-medium">{integrationDisplay.paymentMethodLabel}</span>
              </div>
            ) : (
              <span className="text-muted-foreground">{t('notAvailable')}</span>
            )}
          </div>

          {/* Processor */}
          <div className="flex flex-col">
            <span className="text-muted-foreground mb-1 text-sm">{t('overviewCard.processor')}</span>
            {processor ? (
              <div className="flex items-center">
                <Server className="mr-2 h-5 w-5" />
                <div>
                  <span className="font-medium">{processor.name}</span>
                  <Badge variant="outline" className="ml-2 text-[10px]">{processor.type}</Badge>
                </div>
              </div>
            ) : cryptoDisplay ? (
              <div className="flex items-center">
                <Server className="mr-2 h-5 w-5" />
                <span className="font-medium">{t('table.fallbacks.cryptoOnchain')}</span>
              </div>
            ) : integrationDisplay ? (
              <div className="flex items-center">
                <Server className="mr-2 h-5 w-5" />
                <div>
                  <span className="font-medium">{integrationDisplay.processorLabel}</span>
                  <Badge variant="outline" className="ml-2 text-[10px]">{t('overviewCard.integration')}</Badge>
                </div>
              </div>
            ) : (
              <span className="text-muted-foreground">{t('notAvailable')}</span>
            )}
          </div>

          {/* Initiated By */}
          <div className="flex flex-col">
            <span className="text-muted-foreground mb-1 text-sm">{t('overviewCard.initiatedBy')}</span>
            <div className="flex items-center gap-2">
              {isMIT ? <Repeat className="h-5 w-5" /> : <UserCheck className="h-5 w-5" />}
              <Badge variant={isMIT ? 'yellow' : 'blue'}>
                {isMIT ? t('overviewCard.merchantInitiated') : t('overviewCard.customerInitiated')}
              </Badge>
              {isMIT && (payment as any).reasonType && (
                <Badge variant="outline" className="text-xs">{(payment as any).reasonType}</Badge>
              )}
            </div>
          </div>

          {/* Order */}
          <div className="flex flex-col">
            <span className="text-muted-foreground mb-1 text-sm">{t('orderId')}</span>
            {payment.orderId ? (
              <>
                <div className="flex items-center">
                  <ShoppingCart className="mr-2 h-5 w-5" />
                  <OrderResourceBadge orderId={payment.orderId} variant="outline" />
                </div>
                {payment.order?.externalOrderId && (
                  <div className="mt-1">
                    <CopyableResourceBadge
                      name={t('overviewCard.externalOrder')}
                      value={payment.order.externalOrderId}
                      className="text-xs"
                    />
                  </div>
                )}
              </>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </div>

          {/* Store */}
          <div className="flex flex-col">
            <span className="text-muted-foreground mb-1 text-sm">{t('overviewCard.store')}</span>
            {storeId ? (
              <div className="flex items-center">
                <Store className="mr-2 h-5 w-5" />
                {storeName ? (
                  <StoreResourceBadge storeId={storeId} storeName={storeName} variant="outline" />
                ) : (
                  <CopyableResourceBadge name={t('overviewCard.store')} value={storeId} variant="outline" />
                )}
              </div>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </div>

          {/* Payment Flow */}
          {payment.paymentFlowId && (
            <div className="flex flex-col">
              <span className="text-muted-foreground mb-1 text-sm">{t('overviewCard.paymentFlow')}</span>
              <div className="flex items-center">
                <Workflow className="mr-2 h-5 w-5" />
                <PaymentFlowResourceBadge
                  flowId={payment.paymentFlowId}
                  flowName={payment.paymentFlow?.name}
                  variant="outline"
                />
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
